import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Pages
import LoginPage from "./pages/LoginPage";
import SigningPage from "./pages/SigningPage";
import CompanySelectPage from "./pages/CompanySelectPage";
import DashboardPage from "./pages/DashboardPage";
import CustomersPage from "./pages/CustomersPage";
import CustomerDetailPage from "./pages/CustomerDetailPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import OffersPage from "./pages/OffersPage";
import OfferCreatePage from "./pages/OfferCreatePage";
import OfferDetailPage from "./pages/OfferDetailPage";
import ProductsPage from "./pages/ProductsPage";
import PipelinePage from "./pages/PipelinePage";
import PlanningPage from "./pages/PlanningPage";
import TextBlocksPage from "./pages/TextBlocksPage";
import UsersPage from "./pages/UsersPage";
import CompaniesPage from "./pages/CompaniesPage";
import CompanySettingsPage from "./pages/CompanySettingsPage";
import CoverPageEditorPage from "./pages/CoverPageEditorPage";
import SettingsPage from "./pages/SettingsPage";
import EmailSettingsPage from "./pages/EmailSettingsPage";
import InvoicesPage from "./pages/InvoicesPage";
import FreeOfferCreatePage from "./pages/FreeOfferCreatePage";
import FreeOfferDetailPage from "./pages/FreeOfferDetailPage";
import FreeOfferEditPage from "./pages/FreeOfferEditPage";
import FreeInvoiceCreatePage from "./pages/FreeInvoiceCreatePage";
import ActivityLogPage from "./pages/ActivityLogPage";
import MarginOverviewPage from "./pages/MarginOverviewPage";
import SuppliersPage from "./pages/SuppliersPage";
import CreditNotesPage from "./pages/CreditNotesPage";
import CustomerCreditNotesPage from "./pages/CustomerCreditNotesPage";
import SigningManagementPage from "./pages/SigningManagementPage";
import ProfilePage from "./pages/ProfilePage";
import EmailTrackingPage from "./pages/EmailTrackingPage";
import AppointmentsPage from "./pages/AppointmentsPage";
import AppointmentTemplatesPage from "./pages/AppointmentTemplatesPage";
import ReminderSettingsPage from "./pages/ReminderSettingsPage";
import OfferPortalPage from "./pages/OfferPortalPage";
import LegacyOfferDetailPage from "./pages/LegacyOfferDetailPage";
import LegacyOfferImportPage from "./pages/LegacyOfferImportPage";
import LegacyOfferCreatePage from "./pages/LegacyOfferCreatePage";
import OfferImportPage from "./pages/OfferImportPage";
import ImportedOfferDetailPage from "./pages/ImportedOfferDetailPage";
import EmailCsvImportPage from "./pages/EmailCsvImportPage";
import MessagingSettingsPage from "./pages/MessagingSettingsPage";

// Heat Pump (WP) Offer Pages
import WPOffersListPage from "./pages/WPOffersListPage";
import WPOfferCreatePage from "./pages/WPOfferCreatePage";
import WPOfferDetailPage from "./pages/WPOfferDetailPage";
import CombinedOfferImportReviewPage from "./pages/CombinedOfferImportReviewPage";
import ReonicImportPage from "./pages/ReonicImportPage";

// Layout
import MainLayout from "./components/layout/MainLayout";

// Protected Route Component
const ProtectedRoute = ({ children, requireCompany = true }) => {
  const { user, isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if company selection is required
  if (requireCompany && user?.role !== "superadmin" && !user?.active_company_id && user?.company_ids?.length > 1) {
    return <Navigate to="/select-company" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const { isAuthenticated, user } = useAuth();
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
      } />
      
      {/* Public Signing Page - No authentication required */}
      <Route path="/sign/:requestId" element={<SigningPage />} />
      
      {/* Public Offer Portal - Self-service for customers */}
      <Route path="/angebot-aufrufen" element={<OfferPortalPage />} />
      
      {/* Company Selection */}
      <Route path="/select-company" element={
        <ProtectedRoute requireCompany={false}>
          <CompanySelectPage />
        </ProtectedRoute>
      } />
      
      {/* Protected Routes with Layout */}
      <Route path="/" element={
        <ProtectedRoute>
          <MainLayout />
        </ProtectedRoute>
      }>
        <Route index element={<DashboardPage />} />
        <Route path="kunden" element={<CustomersPage />} />
        <Route path="kunden/:id" element={<CustomerDetailPage />} />
        <Route path="termine" element={<AppointmentsPage />} />
        <Route path="termine/erinnerungen" element={<ReminderSettingsPage />} />
        <Route path="projekte" element={<ProjectsPage />} />
        <Route path="projekte/:id" element={<ProjectDetailPage />} />
        <Route path="angebote" element={<OffersPage />} />
        <Route path="angebote/neu" element={<OfferCreatePage />} />
        <Route path="angebote/neu/:projectId" element={<OfferCreatePage />} />
        <Route path="angebote/:id" element={<OfferDetailPage />} />
        <Route path="angebote/:id/bearbeiten" element={<OfferCreatePage />} />
        <Route path="freitext-angebote/neu" element={<FreeOfferCreatePage />} />
        <Route path="freitext-angebote/:id" element={<FreeOfferDetailPage />} />
        <Route path="freitext-angebote/:id/bearbeiten" element={<FreeOfferEditPage />} />
        <Route path="wp-angebote" element={<WPOffersListPage />} />
        <Route path="wp-angebote/neu" element={<WPOfferCreatePage />} />
        <Route path="wp-angebote/:id" element={<WPOfferDetailPage />} />
        <Route path="rechnungen" element={<InvoicesPage />} />
        <Route path="freitext-rechnungen/neu" element={<FreeInvoiceCreatePage />} />
        <Route path="lieferanten" element={<SuppliersPage />} />
        <Route path="gutschriften" element={<CreditNotesPage />} />
        <Route path="gutschriften-kunden" element={<CustomerCreditNotesPage />} />
        <Route path="signaturen" element={<SigningManagementPage />} />
        <Route path="produkte" element={<ProductsPage />} />
        <Route path="pipeline" element={<PipelinePage />} />
        <Route path="planung" element={<PlanningPage />} />
        <Route path="textbausteine" element={<TextBlocksPage />} />
        <Route path="aktivitaetsprotokoll" element={<ActivityLogPage />} />
        <Route path="margen" element={<MarginOverviewPage />} />
        <Route path="benutzer" element={<UsersPage />} />
        <Route path="firmen" element={<CompaniesPage />} />
        <Route path="firmen-ci" element={<CompanySettingsPage />} />
        <Route path="firmen-ci/deckblatt" element={<CoverPageEditorPage />} />
        <Route path="einstellungen" element={<SettingsPage />} />
        <Route path="einstellungen/email" element={<EmailSettingsPage />} />
        <Route path="einstellungen/email-tracking" element={<EmailTrackingPage />} />
        <Route path="einstellungen/termin-vorlagen" element={<AppointmentTemplatesPage />} />
        <Route path="einstellungen/messaging" element={<MessagingSettingsPage />} />
        <Route path="profil" element={<ProfilePage />} />
        <Route path="legacy-angebote/:id" element={<LegacyOfferDetailPage />} />
        <Route path="legacy-angebote/import/:customerId" element={<LegacyOfferImportPage />} />
        <Route path="legacy-angebote/neu/:customerId" element={<LegacyOfferCreatePage />} />
        
        {/* New Offer Import System V2 */}
        <Route path="angebote-import" element={<OfferImportPage />} />
        <Route path="angebote-import/kombi/:importFileId" element={<CombinedOfferImportReviewPage />} />
        <Route path="angebote-import/:id" element={<ImportedOfferDetailPage />} />
        <Route path="reonic-import" element={<ReonicImportPage />} />
        
        {/* Email CSV Import - Automatischer Lead-Import */}
        <Route path="email-csv-import" element={<EmailCsvImportPage />} />
      </Route>
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
